import { Check, Close, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, useEventCallback } from '@mui/material';
import { memo, useState, useCallback, useMemo } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import MarriageForm from './form';
import { AccordionTitle } from 'shell/components/shared/accordion-title';
import {
  AccordionMarriage,
  RegistrationMarriageFormVM,
} from 'mutation/view-models/registration-marriage';
import SearchMarriageForm from './search';
import { isObjectEmpty } from 'shell/utils/is-object-empty';
import { ActType } from 'shell/constants/act-type';

interface MarriageProps {
  disabled?: boolean;
  isEditMode?: boolean;
  isFromCivilStatus?: boolean;
}

const MarriageComponent = ({
  disabled,
  isEditMode,
  isFromCivilStatus,
}: MarriageProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);

  const formContext = useFormikContext<RegistrationMarriageFormVM>();

  const onFieldMarriageChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('marriage', {
        ...formContext.values.marriage,
        [key]: value,
      });
    },
    [formContext.values.marriage, formContext.setFieldValue]
  );

  const onFieldsMarriageChange = useCallback(
    (values) => {
      formContext.setFieldValue('marriage', {
        ...formContext.values.marriage,
        ...values,
      });
    },
    [formContext.values.marriage, formContext.setFieldValue]
  );

  const onFieldMarriageSearchChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('searchMarriage', {
        ...formContext.values.searchMarriage,
        [key]: value,
      });
    },
    [formContext.values.searchMarriage, formContext.setFieldValue]
  );

  const onChangeValues = useEventCallback((values: RegistrationMarriageFormVM) => {
    const marriageData = formContext.values.marriage || {};

    const foreign = isFromCivilStatus
      ? marriageData?.foreign
      : values.marriage?.foreign?.state?.id
      ? values.marriage?.foreign
      : marriageData.foreign;

    const digital = !isObjectEmpty((values.marriage?.digital || {}) as Record<string, unknown>)
      ? values.marriage?.digital
      : marriageData.digital;

    const paper = !isObjectEmpty((values.marriage?.paper || {}) as Record<string, unknown>)
      ? values.marriage?.paper
      : marriageData.paper;

    formContext.setValues({
      ...formContext.values,
      ...values,
      marriage: {
        ...marriageData,
        ...(values.marriage || {}),
        eventDate: isFromCivilStatus
          ? marriageData?.eventDate
          : values.marriage?.eventDate || marriageData.eventDate,
        eventChoiceDate: isFromCivilStatus
          ? marriageData?.eventChoiceDate
          : values.marriage?.eventChoiceDate || marriageData.eventChoiceDate,
        isForeign: isFromCivilStatus ? marriageData?.isForeign : Boolean(foreign?.state?.id),
        italian: isFromCivilStatus
          ? marriageData?.italian
          : values.marriage?.italian?.municipality?.description
          ? values.marriage?.italian
          : marriageData.italian,
        foreign,
        actType: digital?.idAnsc
          ? ActType.AttoDigitale
          : paper?.actNumber
          ? ActType.AttoCartaceo
          : ActType.SenzaAtto,
        digital: digital,
        paper: paper,
      },
    });
  });

  const isOpenStartAccordion: boolean = useMemo(() => {
    return !Boolean(formContext.values.marriage?.idAnprSoggetto);
  }, [formContext.values.marriage?.idAnprSoggetto]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-marriage-content"
        id="panel-person-marriage-header"
        expandIcon={<ExpandMore />}
      >
        {formContext.errors.marriage ? <Close color="error" /> : <Check color="success" />}
        <AccordionTitle isEditMode={isEditMode}>Matrimonio</AccordionTitle>
      </AccordionSummary>
      <AccordionDetails>
        <SearchMarriageForm
          values={formContext.values.searchMarriage}
          onChange={onFieldMarriageSearchChange}
          onChangeValues={onChangeValues}
          disabled={disabled}
          isOpenStartAccordion={isOpenStartAccordion}
        />
        <MarriageForm
          disabled={disabled}
          values={formContext.values.marriage}
          onChange={onFieldMarriageChange}
          onChangeValues={onFieldsMarriageChange}
          isEditMode={isEditMode}
          errors={formContext.errors.marriage as FormikErrors<AccordionMarriage>}
          touched={formContext.touched.marriage as unknown as FormikTouched<AccordionMarriage>}
        />
      </AccordionDetails>
    </Accordion>
  );
};

MarriageComponent.displayName = 'Accordion marriage component';

const Marriage = memo(MarriageComponent);

export default Marriage;
